import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '@/utils/cn';

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TermsModal = ({ isOpen, onClose }: TermsModalProps) => {
  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 z-[130]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            className="fixed inset-0 z-[130] flex items-end justify-center p-0"
            style={{ paddingTop: 'calc(var(--onboarding-header-h, 110px) + 16px)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <motion.div
              className={cn(
                'bg-white rounded-t-2xl shadow-xl w-full max-w-none max-h-[90vh]',
                'relative flex flex-col'
              )}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              transition={{ type: 'spring', stiffness: 320, damping: 32 }}
              onClick={(e) => e.stopPropagation()}
              dir="rtl"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-base font-bold text-gray-900 font-persian">
                  شرایط و قوانین استفاده
                </h2>
                <button
                  onClick={onClose}
                  className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 p-4 overflow-y-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                <div className="space-y-2.5 text-gray-700 font-persian leading-relaxed">
                  <h3 className="text-sm font-semibold text-gray-900">
                    ۱. پذیرش قوانین
                  </h3>
                  <p className="text-xs">
                    به بوکا خوش آمدید! ثبت نام شما به معنای مطالعه و پذیرش کامل قوانین و شرایط زیر است. لطفاً این موارد را با دقت مطالعه فرمایید.
                  </p>

                  <h3 className="text-sm font-semibold text-gray-900">
                    ۲. حقوق مؤلف و محتوای بوکا
                  </h3>
                  <ul className="list-disc list-inside space-y-1 mr-3 text-xs">
                    <li>
                      محتوای ارائه شده در بوکا، شامل خلاصه‌ها و سؤالات، آثاری مستقل هستند که بر پایه مطالعه و تحلیل کتاب‌های اصلی و با استفاده از منابع مختلف، هوش مصنوعی و نظارت کارشناسان انسانی تولید شده‌اند. این محتوا صرفاً دیدگاه‌ها و برداشت‌های کلیدی آثار اصلی را منتقل می‌کند و جایگزین مطالعه کامل کتاب نیست.
                    </li>
                    <li>
                      ما معتقدیم این خلاصه‌ها نه تنها به حقوق ناشر و نویسنده لطمه نمی‌زند، بلکه با معرفی کتاب‌ها به مخاطبان جدید، به افزایش فروش نسخه‌های اصلی نیز کمک می‌کند.
                    </li>
                    <li>
                      با توجه به اینکه بخشی از فرآیند تولید محتوا توسط هوش مصنوعی انجام می‌شود، ممکن است درصدی از خطا در خلاصه‌ها یا سؤالات وجود داشته باشد. بوکا مسئولیتی در قبال اشتباهات جزئی احتمالی بر عهده نمی‌گیرد اما همواره برای بهبود کیفیت محتوای خود تلاش می‌کند.
                    </li>
                  </ul>
                  <p className="font-medium text-xs">
                    کلیه حقوق مادی و معنوی این محتوای تولیدی منحصراً متعلق به بوکا است و استفاده از محتوای بوکا خارج از این پلتفرم (انتشار، کپی، فروش، بازتولید و ...) تحت هر شرایطی ممنوع است و در صورت تخلف، پیگیری قانونی انجام خواهد شد.
                  </p>

                  <h3 className="text-sm font-semibold text-gray-900">
                    ۳. شرایط استفاده
                  </h3>
                  <ul className="list-disc list-inside space-y-1 mr-3 text-xs">
                    <li>
                      حساب کاربری تنها مخصوص یک نفر است. به‌اشتراک‌گذاشتن نام کاربری و رمز عبور با دیگران ممنوع است و موجب مسدودشدن دائمی حساب می‌شود.
                    </li>
                    <li>
                      کاربر حق ندارد هیچ‌گونه محتوایی منتشر کند که ناقض حقوق اشخاص ثالث، حاوی مطالب توهین‌آمیز، سیاسی، مذهبی یا خلاف قوانین کشور باشد.
                    </li>
                    <li>
                      در صورت تخلف کاربر، بوکا می‌تواند حساب کاربری او را حذف کند و در این شرایط وجه پرداختی بازگردانده نخواهد شد.
                    </li>
                    <li>
                      مسئولیت کلیه‌ی فعالیت‌هایی که از طریق حساب کاربری انجام می‌شود، بر عهده‌ی خود کاربر است.
                    </li>
                  </ul>

                  <h3 className="text-sm font-semibold text-gray-900">
                    ۴. خرید و اشتراک
                  </h3>
                  <ul className="list-disc list-inside space-y-1 mr-3 text-xs">
                    <li>
                      اشتراک خریداری‌شده فقط به یک کاربر اختصاص دارد و انتقال یا فروش آن به دیگران مجاز نیست.
                    </li>
                    <li>
                      شرایط و هزینه‌های اشتراک ممکن است توسط بوکا تغییر کند. هرگونه تغییر از طریق اپلیکیشن اطلاع‌رسانی خواهد شد.
                    </li>
                    <li>
                      در صورتی که به دلیل تخلف کاربر حساب مسدود شود، هزینه‌ی پرداختی مسترد نخواهد شد.
                    </li>
                  </ul>

                  <h3 className="text-sm font-semibold text-gray-900">
                    ۵. حریم خصوصی
                  </h3>
                  <ul className="list-disc list-inside space-y-1 mr-3 text-xs">
                    <li>
                      حفاظت از اطلاعات شخصی کاربران اولویت اصلی بوکاست. اطلاعات شما به‌صورت ایمن نگهداری می‌شود و جز در موارد الزام قانونی (مانند درخواست مراجع قضایی ذی‌صلاح)، در اختیار شخص یا سازمان دیگری قرار نخواهد گرفت.
                    </li>
                    <li>
                      بوکا ممکن است از برخی اطلاعات کاربران برای بهبود خدمات و تحقیقات بازاریابی استفاده کند.
                    </li>
                  </ul>

                  <h3 className="text-sm font-semibold text-gray-900">
                    ۶. تغییر قوانین
                  </h3>
                  <p className="text-xs">
                    بوکا این حق را دارد که در هر زمان قوانین و شرایط استفاده را تغییر دهد. نسخه‌ی به‌روز این قوانین در اپلیکیشن منتشر خواهد شد و ادامه‌ی استفاده از سرویس به‌معنای پذیرش آن است.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div
                className="flex-shrink-0 p-4 pb-0 border-t border-gray-200"
                style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}
              >
                <button
                  onClick={onClose}
                  className="w-full bg-primary-500 hover:bg-primary-600 active:bg-primary-700 text-white font-persian font-medium text-sm py-2.5 px-4 rounded-lg transition-colors"
                >
                  خواندم
                </button>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default TermsModal;
